import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { ImageRecord, VideoRecord } from "../lib/types";

export default function LibraryTab({
  onAnimate,
  onExtend,
  onError,
}: {
  onAnimate: (rec: ImageRecord) => void;
  onExtend: (rec: VideoRecord) => void;
  onError: (e: unknown) => void;
}) {
  const [images, setImages] = useState<ImageRecord[]>([]);
  const [videos, setVideos] = useState<VideoRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const h = await api.history();
        setImages(h.images);
        setVideos(h.videos);
      } catch (e) {
        onError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [onError]);

  if (loading) return <div className="p-4 text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="h-full overflow-y-auto p-4 space-y-6">
      <section>
        <h2 className="text-sm font-semibold text-zinc-300 mb-2">Videos</h2>
        {videos.length === 0 ? (
          <p className="text-zinc-500 text-sm">No videos yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {videos.map((v) => (
              <div key={v.id} className="card overflow-hidden flex flex-col">
                <div className="aspect-video bg-zinc-950 flex items-center justify-center">
                  {v.status === "completed" && v.url ? (
                    <video src={v.url} controls preload="metadata" className="w-full h-full object-contain" />
                  ) : v.status === "failed" ? (
                    <span className="text-rose-400 text-xs">Failed</span>
                  ) : (
                    <span className="text-zinc-500 text-xs">{v.status}</span>
                  )}
                </div>
                <div className="p-3 text-xs text-zinc-400 flex flex-col gap-2 flex-1">
                  <div className="flex gap-1 flex-wrap">
                    <span className="chip">{v.kind}</span>
                    {v.duration && <span className="chip">{v.duration}s</span>}
                  </div>
                  <div className="line-clamp-3 text-zinc-300">{v.prompt}</div>
                  <div className="flex gap-2 mt-auto">
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => onExtend(v)}
                      disabled={v.status !== "completed"}
                    >
                      Extend
                    </button>
                    {v.url && (
                      <a className="btn-ghost text-xs" href={v.url} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold text-zinc-300 mb-2">Images</h2>
        {images.length === 0 ? (
          <p className="text-zinc-500 text-sm">No images yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {images.map((img) => (
              <div key={img.id} className="card overflow-hidden flex flex-col">
                <img src={img.url} alt={img.prompt} loading="lazy" className="w-full aspect-square object-cover bg-zinc-950" />
                <div className="p-2 text-xs flex flex-col gap-2 flex-1">
                  <div className="line-clamp-2 text-zinc-300">{img.prompt}</div>
                  <div className="flex gap-1 mt-auto">
                    <button className="btn-secondary text-xs" onClick={() => onAnimate(img)}>
                      Animate
                    </button>
                    <a className="btn-ghost text-xs" href={img.url} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
